/* globals Routes */

import { ButtonGroup } from "common/styleguide/ButtonGroup";
import { CheckboxGroup } from "common/styleguide/CheckboxGroup";
import { OrderedMap, Set } from "immutable";
import { commonNouns, commonVerbs } from "lib/localization";
import React from "react";
import { defineMessages, FormattedMessage as T, injectIntl } from "react-intl";
import { plannerMessages } from "../intlMessages";
import { RadioButtonGroupWithTooltips } from "./RadioButtonGroupWithTooltips";
import styles from "./Sidebar.scss";

export const messages = defineMessages({
  walking: {
    id: "Sidebar.walking",
    defaultMessage: "Walking",
  },
  driving: {
    id: "Sidebar.driving",
    defaultMessage: "Driving",
  },
  drawLines: {
    id: "Sidebar.drawLines",
    defaultMessage: "Draw Lines",
  },
  createPoi: {
    id: "Sidebar.createPoi",
    defaultMessage: "Add POI",
  },
  addCue: {
    id: "Sidebar.addCue",
    defaultMessage: "Add to Cuesheet",
  },
  createControlPoint: {
    id: "Sidebar.createControlPoint",
    defaultMessage: "Add/Remove Control Point",
  },
  followRoadsTip: {
    id: "Sidebar.followRoadsTip",
    defaultMessage:
      "Click along the roads you want to travel on and we'll generate a cuesheet and elevation profile as you plan. Feel free to click and drag your route around to correct any mistakes. Avoid clicking intersections as this can confuse the planner and result in incorrect cuesheet entries.",
  },
  drawLinesTip: {
    id: "Sidebar.drawLinesTip",
    defaultMessage:
      "Use draw lines when the roads aren’t quite what you want (or when Google Maps doesn't know about them yet). Just click along your path and we’ll connect the dots, even it it means going right through the park or down a street that might be closed to traffic.",
  },
  createPoiTip: {
    id: "Sidebar.createPoiTip",
    defaultMessage:
      "POIs are points of interest along a route, helpful for navigation, showing water or food stops, and more. Click any point on the map to drop a POI.",
  },
  addCueTip: {
    id: "Sidebar.addCueTip",
    defaultMessage:
      "Occasionally you'll want to manually add a line to the cuesheet. Just click along the route where you want a custom entry, then fill in the box that pops up.",
  },
  createControlPointTip: {
    id: "Sidebar.createControlPointTip",
    defaultMessage:
      "Control points are points that bound sections of your route together. By adding a control point to the route, clicking and drag editing will only affect the section bounded between the two nearest control points.",
  },
});

messages.followRoads = commonVerbs.followRoads;
messages.cycling = commonNouns.cycling;
messages.avoidHighways = plannerMessages.avoidHighways;

export class _Sidebar extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      selectedOptimizeFor: props.optimizeForDefault || "cycling",
      selectedCommand: props.selectedCommand,
      selectedRouteOptions: Set(["avoidHighways"]),
      showRouteOptions: props.showRouteOptions !== false,
    };
  }

  onOptimizeChange = (selectedButton) => {
    this.setState({ selectedOptimizeFor: selectedButton });

    if (this.props.optimizeForCallback) {
      this.props.optimizeForCallback.call(null, selectedButton);
    }
  };

  selectCommand = (selectedCommand) => {
    this.setState({ selectedCommand: selectedCommand });
  };

  onAvoidHighwaysChange = (routeOptions) => {
    this.setState({ selectedRouteOptions: routeOptions });
    this.props.avoidHighwaysChanged.call(null, "avoidHighways", routeOptions.has("avoidHighways"));
  };

  componentDidMount = () => {
    // don't clobber this when in cue review mode, until we make new sidebar work with cue review
    if (this.state.showRouteOptions) {
      window.rwgps.Sidebar = {
        selectCommand: (command) => this.selectCommand(command),
      };
    }
    this.applyCommand(this.state.selectedCommand);
  };

  componentWillUnmount = () => {
    this.unApplyCommand(this.state.selectedCommand);

    // don't clobber this when in cue review mode, until we make new sidebar work with cue review
    if (this.state.showRouteOptions) {
      delete window.rwgps.Sidebar;
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { selectedCommand } = this.state;

    this.unApplyCommand(prevState.selectedCommand);
    this.applyCommand(selectedCommand);
  };

  unApplyCommand = (command) => {
    const { unsetCallbacks } = this.props;

    // because legacy stuff, see if there is an undo callback for this specific option
    if (unsetCallbacks && unsetCallbacks[command]) {
      unsetCallbacks[command].apply();
      delete Routes.New.mapCommand.selectedCommand;
    }
  };

  applyCommand = (command) => {
    const { setCallbacks } = this.props;

    // because legacy stuff, see if there is a callback for this specific option
    if (setCallbacks && setCallbacks[command]) {
      setCallbacks[command].apply(null);
      Routes.New.mapCommand.selectedCommand = command;
    }
  };

  render() {
    const { selectedCommand, selectedOptimizeFor, selectedRouteOptions, showRouteOptions } = this.state;
    const { setCallbacks, intl } = this.props;

    const fromKeysToTranslatedMap = (keys) => OrderedMap(keys.map((k) => [k, intl.formatMessage(messages[k])]));

    const optimizeForTypes = fromKeysToTranslatedMap(["walking", "cycling", "driving"]);
    const commands = fromKeysToTranslatedMap(["followRoads", "drawLines", "createPoi", "addCue", "createControlPoint"]);
    const commandTooltips = OrderedMap(
      ["followRoads", "drawLines", "createPoi", "addCue", "createControlPoint"].map((k) => [
        k,
        intl.formatMessage(messages[`${k}Tip`]),
      ])
    );
    const routeOptions = fromKeysToTranslatedMap(["avoidHighways"]);

    const filteredCommands = commands.filter(
      (fn, key) => Object.keys(setCallbacks).findIndex((callbackKey) => callbackKey === key) >= 0
    );

    const optimizeOptions = showRouteOptions && (
      <div>
        <h2>
          <T id="Sidebar.optimizeFor" defaultMessage="Optimize for" />:
        </h2>
        <ButtonGroup
          options={optimizeForTypes}
          selected={selectedOptimizeFor}
          onChange={this.onOptimizeChange}
          squish
          flex
        />
        <CheckboxGroup
          options={routeOptions}
          selected={selectedRouteOptions}
          onChange={this.onAvoidHighwaysChange}
          horizontal
        />
      </div>
    );

    return (
      <div className={styles.Sidebar}>
        <h2>
          <T id="Sidebar.clickTo" defaultMessage="Click to" />:
        </h2>
        <RadioButtonGroupWithTooltips
          name="plannerCommands"
          options={filteredCommands}
          selected={selectedCommand}
          onChange={this.selectCommand}
          tooltips={commandTooltips}
          tooltipOptions={{ placement: "left" }}
        />
        {optimizeOptions}
      </div>
    );
  }
}

export const Sidebar = injectIntl(_Sidebar);
